/**
 * useNpsFrequency — ports-owned NPS eligibility (replaces legacy `useNPSFrequency`).
 *
 * Rules: minimum validated reports (API), 30d since last submit, 15d since local dismiss.
 * Uses redux selector + idle (no `useApiStatus` / DialogM). Relative `data/` imports only.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { npsService } from "../../../../data/services/npsService"
import { getFromStorage, setToStorage } from "../../../../data/utility/asyncStorageUtils"

const STORAGE_KEY_DISMISSED = "@last_nps_dismissed_date"
const FREQUENCY_DAYS_SUBMITTED = 30
const FREQUENCY_DAYS_DISMISSED = 15

type LastSubmissionData = {
    has_minimum_reports?: boolean
    has_submission?: boolean
    days_since_last?: number
}

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: LastSubmissionData
}

export function useNpsFrequency(userEmail: string | null = null) {
    const dispatch = useDispatch()
    const [canShowNPS, setCanShowNPS] = useState(false)
    const handledSuccessRef = useRef(false)
    const handledErrorRef = useRef(false)

    const lastSubmissionReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.getLastSubmission as ApiSlice | undefined
    )

    const checkEligibility = useCallback(async () => {
        handledSuccessRef.current = false
        handledErrorRef.current = false
        const urlParams = userEmail ? { email: userEmail } : {}
        dispatch(apiRequest(npsService.getLastSubmission, {}, {}, urlParams) as never)
    }, [dispatch, userEmail])

    useEffect(() => {
        if (!lastSubmissionReq?.success || handledSuccessRef.current) return
        handledSuccessRef.current = true
        const data = lastSubmissionReq.data
        dispatch(apiIdle(npsService.getLastSubmission) as never)

        void (async () => {
            if (!data?.has_minimum_reports) {
                setCanShowNPS(false)
                return
            }
            if (
                data.has_submission &&
                data.days_since_last != null &&
                data.days_since_last < FREQUENCY_DAYS_SUBMITTED
            ) {
                setCanShowNPS(false)
                return
            }
            const lastDismissed = await getFromStorage(STORAGE_KEY_DISMISSED)
            if (lastDismissed) {
                const daysSince = moment().diff(moment(lastDismissed), "days")
                if (daysSince < FREQUENCY_DAYS_DISMISSED) {
                    setCanShowNPS(false)
                    return
                }
            }
            setCanShowNPS(true)
        })()
    }, [dispatch, lastSubmissionReq?.success, lastSubmissionReq?.data])

    useEffect(() => {
        if (!lastSubmissionReq?.error || handledErrorRef.current) return
        handledErrorRef.current = true
        dispatch(apiIdle(npsService.getLastSubmission) as never)
        // On error, gracefully allow showing (legacy parity).
        setCanShowNPS(true)
    }, [dispatch, lastSubmissionReq?.error])

    const markAsDismissed = useCallback(async () => {
        await setToStorage(STORAGE_KEY_DISMISSED, moment().toISOString())
        setCanShowNPS(false)
    }, [])

    return { canShowNPS, checkEligibility, markAsDismissed }
}
